<template>
  <div
    id="rgbImgContainer"
    class="d-flex justify-center align-center w-100 h-100"
  />
</template>
<script>
import * as d3 from 'd3';
import { getRgbImageNaturalSize } from '@/utils/rgbImageNaturalSizeCache.js';

const CAMERA_RENDER_PRESETS = {
  default: {
    renderScaleX: 1.10,
    renderScaleY: 1.10,
    offsetX: 0,
    offsetY: 0,
  },
  m4t: {
    match: { width: 4032, height: 3024 },
    renderScaleX: 2.1,
    renderScaleY: 2.1,
    offsetX: 0,
    offsetY: 0
  }
};

export default {
  props: {
    imgUrl: {
      type: String,
      default: ''
    },
    imgHeight: { type: Number, default: null },
    imgWidth: { type: Number, default: null },
    width: { type: Number, default: 680 },
    height: { type: Number, default: 512 }
    // offsetX: Number,
    // offsetY: Number,
  },

  data: () => ({
    svg: null,
    zoomLayer: null,
    zoomBehavior: null,
    group: null,
    resolvedImgWidth: 4000,
    resolvedImgHeight: 3000,
    translate0: [0, 0], // 이미지를 0, 0 좌표위치로 맞춘다.
    scale0: 0.17 // 680/4000px, width가 4000px인 이미지를 680px으로 view 크기를 변경할 때, 680px에 이미지가 just하게 채워지는 비율
  }),

  computed: {
    activeCameraPreset() {
      const { m4t } = CAMERA_RENDER_PRESETS;
      if (
        this.resolvedImgWidth === m4t.match.width &&
        this.resolvedImgHeight === m4t.match.height
      ) {
        return m4t;
      }
      return CAMERA_RENDER_PRESETS.default;
    },
    // 카메라 장치별 렌더링 스케일
    effectiveImgWidth() {
      return this.resolvedImgWidth * this.activeCameraPreset.renderScaleX;
    },
    effectiveImgHeight() {
      return this.resolvedImgHeight * this.activeCameraPreset.renderScaleY;
    }
  },

  watch: {
    imgUrl: async function (newImgUrl) {
      console.log('[IMAGE ZOOM VIEWER] imgUrl changed:', newImgUrl);
      console.log('[IMAGE ZOOM VIEWER] imgUrl type:', typeof newImgUrl);
      console.log('[IMAGE ZOOM VIEWER] imgUrl length:', newImgUrl?.length);

      if (newImgUrl) {
        await this.resolveImageSize(newImgUrl);
        this.group.selectAll('image').remove();
        this.group
          .append('image')
          .attr('width', this.effectiveImgWidth + 'px')
          .attr('height', this.effectiveImgHeight + 'px')
          .attr('xlink:href', newImgUrl);
        this.applyInitialTransform();
      } else {
        console.log('[IMAGE ZOOM VIEWER] imgUrl이 없어서 이미지 제거');
        this.group.selectAll('image').remove();
      }
    },
    imgWidth: function () {
      this.syncSizeFromProps();
    },
    imgHeight: function () {
      this.syncSizeFromProps();
    }
  },

  async mounted() {
    this.svg = d3
      .select('div#rgbImgContainer')
      .append('svg')
      .attr('width', this.width + 'px')
      .attr('height', this.height + 'px');

    this.svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', this.width + 'px')
      .attr('height', this.height + 'px');

    this.zoomBehavior = d3
      .zoom()
      // 1 미만 축소 허용 (초기 scale0=0.17도 정상 적용)
      .scaleExtent([0.05, 8])
      .on('zoom', () => {
        this.group.attr('transform', d3.event.transform);
        console.log(
          'translate: ' + d3.event.translate + ', scale: ' + d3.event.scale
        );
      });

    this.zoomLayer = this.svg
      .append('g')
      .call(this.zoomBehavior);

    this.group = this.zoomLayer.append('g');

    if (this.imgUrl) {
      await this.resolveImageSize(this.imgUrl);
      this.group
        .append('image')
        .attr('width', this.effectiveImgWidth + 'px')
        .attr('height', this.effectiveImgHeight + 'px')
        .attr('xlink:href', this.imgUrl);
      this.applyInitialTransform();
    }
  },
  methods: {
    syncSizeFromProps() {
      const width = Number(this.imgWidth);
      const height = Number(this.imgHeight);
      if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
        this.resolvedImgWidth = width;
        this.resolvedImgHeight = height;
      }
    },
    async resolveImageSize(imageUrl) {
      this.syncSizeFromProps();
      // props에 값이 있으면 그대로 사용
      if (this.resolvedImgWidth && this.resolvedImgHeight && this.imgWidth && this.imgHeight) {
        return;
      }
      // props에 값이 없으면 URL로 실제 이미지 크기 추출
      try {
        const size = await this.loadImageNaturalSize(imageUrl);
        if (size) {
          this.resolvedImgWidth = size.width;
          this.resolvedImgHeight = size.height;
        }
      } catch (e) {
        // 실패 시 기존 기본값 유지
      }
    },
    loadImageNaturalSize(imageUrl) {
      return getRgbImageNaturalSize(imageUrl);
    },
    applyInitialTransform() {
      if (!this.zoomLayer || !this.zoomBehavior) return;
      const renderedWidth = this.effectiveImgWidth * this.scale0;
      const renderedHeight = this.effectiveImgHeight * this.scale0;
      const centeredX = (this.width - renderedWidth) / 2 + this.activeCameraPreset.offsetX;
      const centeredY = (this.height - renderedHeight) / 2 + this.activeCameraPreset.offsetY;
      const initialTransform = d3.zoomIdentity
        .translate(centeredX, centeredY)
        .scale(this.scale0);
      this.zoomLayer.call(this.zoomBehavior.transform, initialTransform);
    },
    zoom() {
      this.group.attr(
        'transform',
        'translate(' + d3.event.translate + ')scale(' + d3.event.scale + ')'
      );
      console.log(
        'translate: ' + d3.event.translate + ', scale: ' + d3.event.scale
      );
    }
  }
};
</script>
<style scoped>
.overlay {
  fill: none;
  pointer-events: all;
}
</style>
